import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";

export interface LatLng {
  lat: number;
  lng: number;
}

export interface RoofDrawMapHandle {
  flyTo: (lat: number, lng: number, zoom?: number) => void;
  getMap: () => maplibregl.Map | null;
  clearLabels: () => void;
  /** Zeichnen-Modus von außen aktivieren (z. B. wenn der Nutzer „+ Fläche" klickt). */
  startDraw: () => void;
}

export interface RoofDrawMapProps {
  /** Kartenmittelpunkt – default: Innsbruck */
  center?: LatLng;
  /** Vorhandenes Polygon (wird beim ersten Render geladen) */
  initialPolygon?: LatLng[];
  /** Callback wenn Polygon gezeichnet/bearbeitet wurde */
  onPolygonChange: (coords: LatLng[], areaM2: number) => void;
  /** Kartenhöhe in Pixeln (default: 520) */
  height?: number;
  /** Gebäude-Grundriss als roter Referenz-Umriss (bleibt beim Dach-Zeichnen sichtbar) */
  referencePolygon?: LatLng[];
}

// ── Kanten-Bemaßung Hilfsfunktionen ──────────────────────────────────────────

function haversineM(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) *
      Math.cos(b.lat * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function edgeLabels(
  coords: LatLng[],
): Array<{ lng: number; lat: number; label: string }> {
  return coords
    .map((c, i) => {
      const next = coords[(i + 1) % coords.length];
      if (!next) return null;
      const midLat = (c.lat + next.lat) / 2;
      const midLng = (c.lng + next.lng) / 2;
      const dist = haversineM(c, next);
      return {
        lat: midLat,
        lng: midLng,
        label:
          dist < 1
            ? `${(dist * 100).toFixed(0)} cm`
            : `${dist.toFixed(2)} m`,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
}

const LABEL_SOURCE = "edge-labels-source";
const LABEL_LAYER  = "edge-labels-layer";

function updateEdgeLabels(map: maplibregl.Map, coords: LatLng[]) {
  const points = edgeLabels(coords);
  const geojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: points.map((p) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { label: p.label },
    })),
  };

  const existing = map.getSource<maplibregl.GeoJSONSource>(LABEL_SOURCE);
  if (existing) {
    existing.setData(geojson);
  } else {
    map.addSource(LABEL_SOURCE, { type: "geojson", data: geojson });
    map.addLayer({
      id: LABEL_LAYER,
      type: "symbol",
      source: LABEL_SOURCE,
      layout: {
        "text-field": ["get", "label"],
        "text-size": 11,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#d97706",
        "text-halo-color": "#ffffff",
        "text-halo-width": 2,
      },
    });
  }
}

function clearEdgeLabels(map: maplibregl.Map) {
  const src = map.getSource<maplibregl.GeoJSONSource>(LABEL_SOURCE);
  if (src) {
    src.setData({ type: "FeatureCollection", features: [] });
  }
}

// ── Gebäude-Grundriss als roter Referenz-Umriss ──────────────────────────────

const BUILDING_REF_SOURCE = "building-ref-source";
const BUILDING_REF_FILL = "building-ref-fill";
const BUILDING_REF_OUTLINE = "building-ref-outline";

function ringFromLatLng(coords: LatLng[]): [number, number][] {
  const first = coords[0];
  return [
    ...coords.map((c) => [c.lng, c.lat] as [number, number]),
    [first?.lng ?? 0, first?.lat ?? 0],
  ];
}

function updateBuildingRef(map: maplibregl.Map, coords: LatLng[] | undefined) {
  const fc: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features:
      coords && coords.length >= 3
        ? [
            {
              type: "Feature",
              properties: {},
              geometry: { type: "Polygon", coordinates: [ringFromLatLng(coords)] },
            },
          ]
        : [],
  };

  const existing = map.getSource<maplibregl.GeoJSONSource>(BUILDING_REF_SOURCE);
  if (existing) {
    existing.setData(fc);
  } else {
    map.addSource(BUILDING_REF_SOURCE, { type: "geojson", data: fc });
    map.addLayer({
      id: BUILDING_REF_FILL,
      type: "fill",
      source: BUILDING_REF_SOURCE,
      paint: { "fill-color": "#ef4444", "fill-opacity": 0.08 },
    });
    map.addLayer({
      id: BUILDING_REF_OUTLINE,
      type: "line",
      source: BUILDING_REF_SOURCE,
      paint: { "line-color": "#ef4444", "line-width": 2, "line-dasharray": [3, 2] },
    });
  }
}

// ── Fläche aus Polygon-Koordinaten (Gauß'sche Flächenformel auf WGS84-Näherung)
function calcAreaM2(coords: LatLng[]): number {
  if (coords.length < 3) return 0;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    const ci = coords[i];
    const cj = coords[j];
    if (!ci || !cj) continue;
    const xi = toRad(ci.lng) * Math.cos(toRad(ci.lat)) * R;
    const yi = toRad(ci.lat) * R;
    const xj = toRad(cj.lng) * Math.cos(toRad(cj.lat)) * R;
    const yj = toRad(cj.lat) * R;
    area += xi * yj - xj * yi;
  }
  return Math.abs(area / 2);
}

const SATELLITE_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    esri: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution: "© Esri, Maxar, USGS",
      maxzoom: 19,
    },
  },
  layers: [{ id: "esri-satellite", type: "raster", source: "esri" }],
};

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap",
      maxzoom: 19,
    },
  },
  layers: [{ id: "osm-tiles", type: "raster", source: "osm" }],
};

const CANVAS_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [
    {
      id: "background",
      type: "background",
      paint: { "background-color": "#f8f9fa" },
    },
  ],
};

export const RoofDrawMap = forwardRef<RoofDrawMapHandle, RoofDrawMapProps>(
  function RoofDrawMap(
    {
      center = { lat: 47.2685, lng: 11.3943 },
      initialPolygon,
      onPolygonChange,
      height = 520,
      referencePolygon,
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<maplibregl.Map | null>(null);
    const drawRef = useRef<TerraDraw | null>(null);
    // Zeichnen-Modus, der angefordert wurde, BEVOR terra-draw bereit war.
    const pendingModeRef = useRef<"polygon" | "select" | null>(null);
    // Der [mapStyle]-Effekt läuft auch beim ersten Mount — dort hat der
    // Map-Konstruktor den (identischen) Satelliten-Style aber schon gesetzt.
    // Ein erneutes setStyle() WÄHREND des initialen Ladens lässt MapLibre den
    // Style „from scratch" neu bauen und verschluckt das einmalige `load`-Event,
    // in dem terra-draw initialisiert wird → Zeichnen reagiert nicht. Darum den
    // ersten Lauf überspringen und nur bei echtem Style-Wechsel umschalten.
    const isFirstStyleRun = useRef(true);
    // Aktueller Gebäude-Grundriss für Re-Render nach Style-Wechsel
    const referencePolygonRef = useRef<LatLng[] | undefined>(referencePolygon);
    referencePolygonRef.current = referencePolygon;
    const [mapStyle, setMapStyle] = useState<"satellite" | "osm" | "canvas">("satellite");
    const [area, setArea] = useState<number>(
      initialPolygon ? calcAreaM2(initialPolygon) : 0,
    );
    const [mode, setMode] = useState<"select" | "polygon">("select");
    const [addressSearch, setAddressSearch] = useState("");
    // Kartenfehler (z. B. WebGL deaktiviert) — Editor bleibt sonst nutzbar.
    const [mapError, setMapError] = useState<string | null>(null);

    // Imperative handle — flyTo von außen aufrufbar
    useImperativeHandle(ref, () => ({
      flyTo: (lat: number, lng: number, zoom = 19) => {
        mapRef.current?.flyTo({
          center: [lng, lat],
          zoom,
          essential: true,
          duration: 1200,
        });
      },
      getMap: () => mapRef.current,
      clearLabels: () => {
        if (mapRef.current) clearEdgeLabels(mapRef.current);
      },
      startDraw: () => {
        if (drawRef.current) drawRef.current.setMode("polygon");
        else pendingModeRef.current = "polygon"; // terra-draw noch nicht bereit
        setMode("polygon");
      },
    }));

    // Karte + terra-draw einmalig initialisieren
    useEffect(() => {
      if (!containerRef.current || mapRef.current) return;

      // WebGL-Verfügbarkeit prüfen — MapLibre wirft sonst beim Konstruktor und
      // (ohne Error-Boundary) bliebe die ganze App leer.
      let webglOk = false;
      try {
        const probe = document.createElement("canvas");
        webglOk = !!(probe.getContext("webgl2") || probe.getContext("webgl"));
      } catch {
        webglOk = false;
      }
      if (!webglOk) {
        setMapError(
          "Kartenansicht nicht verfügbar (WebGL deaktiviert) — bitte Koordinaten/Maße manuell erfassen.",
        );
        return;
      }

      let map: maplibregl.Map;
      try {
        map = new maplibregl.Map({
          container: containerRef.current,
          style: SATELLITE_STYLE,
          center: [center.lng, center.lat],
          zoom: 18,
        });
      } catch {
        setMapError(
          "Kartenansicht nicht verfügbar (WebGL deaktiviert) — bitte Koordinaten/Maße manuell erfassen.",
        );
        return;
      }
      mapRef.current = map;

      map.addControl(
        new maplibregl.NavigationControl({ showCompass: true }),
        "bottom-right",
      );
      map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

      map.on("load", () => {
        // Gebäude-Grundriss-Referenz rendern + nach jedem Style-Wechsel erneut (Satellit↔Canvas)
        updateBuildingRef(map, referencePolygonRef.current);
        map.on("styledata", () => updateBuildingRef(map, referencePolygonRef.current));

        // Roter Gebäude-Pin bei center-Koordinaten
        if (center) {
          const el = document.createElement("div");
          el.style.cssText = `
            width: 24px; height: 24px;
            background: #ef4444; border: 3px solid #fff;
            border-radius: 50% 50% 50% 0; transform: rotate(-45deg);
            box-shadow: 0 2px 6px rgba(0,0,0,0.4); cursor: default;
          `;
          new maplibregl.Marker({ element: el })
            .setLngLat([center.lng, center.lat])
            .addTo(map);
        }

        const draw = new TerraDraw({
          adapter: new TerraDrawMapLibreGLAdapter({ map }),
          modes: [
            new TerraDrawPolygonMode({
              styles: {
                fillColor: "#2563eb",
                fillOpacity: 0.25,
                outlineColor: "#1d4ed8",
                outlineWidth: 2,
                closingPointColor: "#1d4ed8",
                closingPointWidth: 3,
                closingPointOutlineColor: "#ffffff",
                closingPointOutlineWidth: 1,
              },
            }),
            new TerraDrawSelectMode({
              flags: {
                polygon: {
                  feature: {
                    scaleable: true,
                    rotateable: true,
                    draggable: true,
                    coordinates: {
                      midpoints: true,
                      draggable: true,
                      deletable: true,
                    },
                  },
                },
              },
            }),
          ],
        });
        draw.start();
        drawRef.current = draw;
        // Falls der Nutzer den Zeichnen-Button schon geklickt hat, BEVOR die Karte
        // fertig geladen war (terra-draw existierte noch nicht): den gewünschten
        // Modus jetzt nachziehen, sonst „reagiert" das Zeichnen scheinbar nicht.
        if (pendingModeRef.current) {
          draw.setMode(pendingModeRef.current);
          pendingModeRef.current = null;
        }

        // Vorhandenes Polygon laden
        if (initialPolygon && initialPolygon.length >= 3) {
          const first = initialPolygon[0];
          const ring: [number, number][] = [
            ...initialPolygon.map((c) => [c.lng, c.lat] as [number, number]),
            [first?.lng ?? 0, first?.lat ?? 0],
          ];
          draw.addFeatures([
            {
              type: "Feature",
              geometry: { type: "Polygon", coordinates: [ring] },
              properties: { mode: "polygon" },
            },
          ]);
          const lngs = initialPolygon.map((c) => c.lng);
          const lats = initialPolygon.map((c) => c.lat);
          map.fitBounds(
            [
              [Math.min(...lngs), Math.min(...lats)],
              [Math.max(...lngs), Math.max(...lats)],
            ],
            { padding: 60 },
          );
        }

        // Helfer: GeoJSON-Ring → LatLng[] + Fläche
        const extractCoords = (ring: number[][]): LatLng[] =>
          ring
            .slice(0, -1)
            .map((pt) => ({ lat: pt[1] ?? 0, lng: pt[0] ?? 0 }));

        // Polygon fertig gezeichnet
        draw.on("finish", (id) => {
          const snapshot = draw.getSnapshot();
          const feature = snapshot.find((f) => f.id === id);
          if (feature?.geometry.type === "Polygon") {
            const ring = feature.geometry.coordinates[0];
            if (!ring) return;
            const coords = extractCoords(ring);
            const m2 = calcAreaM2(coords);
            setArea(m2);
            onPolygonChange(coords, Math.round(m2));
            updateEdgeLabels(map, coords);
            draw.setMode("select");
            setMode("select");
          }
        });

        // Polygon bearbeitet (Drag / Koordinate verschoben)
        draw.on("change", (ids) => {
          const snapshot = draw.getSnapshot();
          for (const id of ids) {
            const feature = snapshot.find((f) => f.id === id);
            if (feature?.geometry.type === "Polygon") {
              const ring = feature.geometry.coordinates[0];
              if (!ring) continue;
              const coords = extractCoords(ring);
              const m2 = calcAreaM2(coords);
              setArea(m2);
              onPolygonChange(coords, Math.round(m2));
              updateEdgeLabels(map, coords);
            }
          }
        });
      });

      return () => {
        drawRef.current?.stop();
        map.remove();
        mapRef.current = null;
        drawRef.current = null;
      };
      // init once — eslint-plugin-react-hooks ist (noch) nicht konfiguriert
    }, []);

    // center-Prop beobachten — flyTo bei Koordinaten-Änderung
    useEffect(() => {
      if (!mapRef.current || !center) return;
      mapRef.current.flyTo({
        center: [center.lng, center.lat],
        zoom: 19,
        essential: true,
        duration: 1000,
      });
    }, [center?.lat, center?.lng]);

    // Gebäude-Grundriss-Referenz aktualisieren, wenn sich der Footprint ändert
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;
      if (map.isStyleLoaded()) {
        updateBuildingRef(map, referencePolygon);
      } else {
        void map.once("load", () => updateBuildingRef(map, referencePolygon));
      }
    }, [referencePolygon]);

    // Karten-Style wechseln ohne Neuinitialisierung
    useEffect(() => {
      if (!mapRef.current) return;
      // Ersten Lauf überspringen — Konstruktor hat den Style bereits gesetzt.
      if (isFirstStyleRun.current) {
        isFirstStyleRun.current = false;
        return;
      }
      const style =
        mapStyle === "satellite"
          ? SATELLITE_STYLE
          : mapStyle === "osm"
          ? OSM_STYLE
          : CANVAS_STYLE;
      mapRef.current.setStyle(style);
    }, [mapStyle]);

    const handleStartDraw = () => {
      if (drawRef.current) drawRef.current.setMode("polygon");
      else pendingModeRef.current = "polygon"; // Klick vor Karten-Load → nachziehen
      setMode("polygon");
    };

    const handleSelect = () => {
      drawRef.current?.setMode("select");
      setMode("select");
    };

    const handleClear = () => {
      const snap = drawRef.current?.getSnapshot() ?? [];
      const ids = snap
        .map((f) => f.id)
        .filter((id): id is string | number => id !== undefined);
      if (ids.length > 0) drawRef.current?.removeFeatures(ids);
      setArea(0);
      onPolygonChange([], 0);
      if (mapRef.current) clearEdgeLabels(mapRef.current);
    };

    const handleAddressSearch = async () => {
      if (!addressSearch.trim()) return;
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressSearch + ", Österreich")}&format=json&limit=1&countrycodes=at`;
      try {
        const res = await fetch(url, { headers: { "User-Agent": "EnergyOS/1.0" } });
        const data = (await res.json()) as Array<{ lat: string; lon: string }>;
        const [first] = data;
        if (first) {
          mapRef.current?.flyTo({
            center: [parseFloat(first.lon), parseFloat(first.lat)],
            zoom: 19,
            essential: true,
            duration: 1200,
          });
        }
      } catch {
        // ignore network errors
      }
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Toolbar */}
        <div
          style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}
        >
          {/* Karten-Quellen Tabs */}
          <div
            style={{
              display: "flex",
              gap: 2,
              background: "#f0f4ff",
              borderRadius: 6,
              padding: 2,
            }}
          >
            {(["satellite", "osm", "canvas"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setMapStyle(s)}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  borderRadius: 4,
                  border: "none",
                  cursor: "pointer",
                  background: mapStyle === s ? "#2563eb" : "transparent",
                  color: mapStyle === s ? "#fff" : "#374151",
                  fontWeight: mapStyle === s ? 600 : 400,
                }}
              >
                {s === "satellite"
                  ? "Satellit (ESRI)"
                  : s === "osm"
                  ? "Straßenkarte"
                  : "Canvas"}
              </button>
            ))}
          </div>

          <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

          {/* Adresse suchen */}
          <div style={{ display: "flex", gap: 4, flex: 1 }}>
            <input
              value={addressSearch}
              onChange={(e) => setAddressSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleAddressSearch()}
              placeholder="Adresse suchen…"
              style={{
                flex: 1,
                padding: "4px 8px",
                fontSize: 12,
                border: "1px solid #d1d5db",
                borderRadius: 4,
              }}
            />
            <button
              onClick={() => void handleAddressSearch()}
              style={{
                padding: "4px 8px",
                fontSize: 12,
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              🔍
            </button>
          </div>

          <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

          {/* Zeichnen / Auswählen / Löschen */}
          <button
            onClick={handleStartDraw}
            style={{
              padding: "4px 12px",
              fontSize: 12,
              borderRadius: 4,
              border: "1px solid #2563eb",
              cursor: "pointer",
              background: mode === "polygon" ? "#2563eb" : "#fff",
              color: mode === "polygon" ? "#fff" : "#2563eb",
              fontWeight: 600,
            }}
          >
            ✏️ Dach abzeichnen
          </button>
          <button
            onClick={handleSelect}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              borderRadius: 4,
              border: "1px solid #6b7280",
              cursor: "pointer",
              background: mode === "select" ? "#6b7280" : "#fff",
              color: mode === "select" ? "#fff" : "#374151",
            }}
          >
            ↖ Auswählen
          </button>
          <button
            onClick={handleClear}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              borderRadius: 4,
              border: "1px solid #ef4444",
              cursor: "pointer",
              background: "#fff",
              color: "#ef4444",
            }}
          >
            🗑 Löschen
          </button>

          {area > 0 && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#16a34a",
                background: "#f0fdf4",
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid #86efac",
              }}
            >
              ✓ {area.toLocaleString("de-AT", { maximumFractionDigits: 0 })} m²
            </span>
          )}
        </div>

        {/* Karte (oder Fallback, wenn WebGL fehlt) */}
        {mapError ? (
          <div
            style={{
              height,
              width: "100%",
              borderRadius: 8,
              border: "1px solid #dce6f0",
              background: "#f8f9fa",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: 24,
              textAlign: "center",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            <div style={{ fontSize: 28 }}>🗺️</div>
            <div style={{ maxWidth: 420, lineHeight: 1.5 }}>{mapError}</div>
          </div>
        ) : (
          <div
            ref={containerRef}
            style={{
              height,
              width: "100%",
              borderRadius: 8,
              border: "1px solid #dce6f0",
              overflow: "hidden",
            }}
          />
        )}

        {mode === "polygon" && (
          <p style={{ fontSize: 11, color: "#6b7280", margin: 0 }}>
            Klicken Sie auf die Eckpunkte der Dachfläche. Doppelklick zum Abschließen.
          </p>
        )}
      </div>
    );
  },
);
